import React from 'react';
import type { Theme } from '@material-ui/core';
import { TableCell, TableRow, TextField } from '@material-ui/core';
import type {
    KostnadskjemaUserInput,
    LonnsomhetDelsum,
} from '@klp/prosjekt/services/types';
import { useKostnadsKalkyleContext } from '../kostnadskalkyle/context/KostnadsKalkyleProvider';
import { useField, useFormikContext } from 'formik';
import { FormikNumericFormat } from '@klp/formgenerator/components/FormikNumericFormat';
import { LonnsomhetTableRowActions } from './LonnsomhetTableRowActions';
import { isAutoCalculatedDelsum } from './isAutoCalculatedDelsum';
import { Amount } from '@klp/design/Amount';
import {
    handleFormikNumericFieldBlur,
    handleFormikNumericFieldChange,
} from '@forvalter/utils';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import i18n from '@forvalter/i18n/config';

type LonnsomhetTableRowProps = {
    rowKey: string;
    index: number;
    data: LonnsomhetDelsum;
    isLoading: boolean;
    onRemove: () => void;
};

const styles = (theme: Theme) => ({
    row: {
        '& .Mui-error fieldset': {
            borderColor: `${theme.palette.error.main} !important`,
        },
    },
});

export const LonnsomhetTableRow: React.FC<LonnsomhetTableRowProps> = ({
    rowKey,
    index,
    data,
    onRemove,
}) => {
    const { isReadOnly } = useKostnadsKalkyleContext();
    const { handleChange } = useFormikContext<KostnadskjemaUserInput>();
    const [nameField, nameMeta] = useField<string>(`${rowKey}.navn`);
    const classes = useKlpStyles({}, styles);

    return (
        <TableRow key={data.navn} className={classes.row}>
            <TableCell>
                <TextField
                    {...nameField}
                    fullWidth={true}
                    variant="outlined"
                    placeholder={i18n.t('general.name')}
                    disabled={isReadOnly}
                    error={Boolean(nameMeta.touched && nameMeta.error)}
                    helperText={nameMeta.touched && nameMeta.error}
                />
            </TableCell>
            <TableCell>
                {isAutoCalculatedDelsum(index, data.navn) ? (
                    /* Hovedprosjekt (index 0) and MVA rows: read-only, backend-calculated */
                    <TextField
                        fullWidth={true}
                        variant="outlined"
                        disabled={true}
                        value={data.kostnad.toLocaleString('no-NO')}
                        error={data.kostnad < 0}
                        inputProps={{
                            style: { textAlign: 'right' },
                        }}
                    />
                ) : (
                    /* Rows 2..n: editable user input */
                    <FormikNumericFormat
                        id={`${rowKey}.kostnad`}
                        disabled={isReadOnly}
                        variant="outlined"
                        align="right"
                        onChange={(e) => {
                            handleFormikNumericFieldChange(
                                handleChange,
                                e,
                                `${rowKey}.kostnad`,
                            );
                        }}
                        onFocus={(e) => e.target.select()}
                        onBlur={(e) => {
                            handleFormikNumericFieldBlur(e, handleChange);
                        }}
                    />
                )}
            </TableCell>
            <TableCell>
                <FormikNumericFormat
                    id={`${rowKey}.avskrivningstid`}
                    disabled={isReadOnly}
                    variant="outlined"
                    align="right"
                    onChange={(e) => {
                        handleFormikNumericFieldChange(
                            handleChange,
                            e,
                            `${rowKey}.avskrivningstid`,
                        );
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => {
                        handleFormikNumericFieldBlur(e, handleChange);
                    }}
                />
            </TableCell>
            <TableCell>
                <FormikNumericFormat
                    id={`${rowKey}.restverdiIProsent`}
                    disabled={isReadOnly}
                    variant="outlined"
                    align="right"
                    onChange={(e) => {
                        handleFormikNumericFieldChange(
                            handleChange,
                            e,
                            `${rowKey}.restverdiIProsent`,
                        );
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => {
                        handleFormikNumericFieldBlur(e, handleChange);
                    }}
                />
            </TableCell>
            <TableCell align="right">
                <Amount value={data.oktLeiePerAar} round={false} />
            </TableCell>
            <TableCell align="right">
                <Amount value={data.oktLeiePerM2PerAar} round={false} />
            </TableCell>
            <TableCell>
                <LonnsomhetTableRowActions
                    id={data.id}
                    index={index}
                    navn={data.navn}
                    onRemove={onRemove}
                />
            </TableCell>
        </TableRow>
    );
};
